/** @jsx jsx */
import React, { useState, createContext, useEffect } from "react";
import { css, jsx } from "@emotion/core";
import BlockUI from "components/molecules/BlockUi";
import FlexBox from "components/atoms/FlexBox";
import moment from "moment";
import useLoginUser from "hooks/useLoginUser";
import Appbar from "components/organismos/Appbar";
import FormLabel from "components/atoms/Form/FormLabel";
import FormContents from "components/atoms/Form/FormContents";
import { grayScale } from "components/styles";
import TabControl from "components/atoms/TabControl";
import BarcodeRecorder from "./BarcodeRecorder";
import ICCardRecorder from "./ICCardRecorder";
import ICCardRecorderEmptySpecifyCode from "./ICCardRecorder/ICCardRecorderEmptySpecifyCode";
import FaceRecorder from "./FaceRecorder";
import DataTable from "components/organismos/DataTable/DataTable";
import useRecorder from "./hook";
import { Column } from "react-table";
import { utilityColor } from "components/styles";
import ReactTooltip from "react-tooltip";
import BodyText from "components/atoms/BodyText";
import ImageModel from "../ImageModel";

import { useHistory } from "react-router-dom";
import Icon from "components/atoms/Icon";
import TextTooltip from "components/molecules/TextTooltip";
import BarcodeRecorderEmptySpecifyCode from "./BarcodeRecorder/BarcodeRecorderEmptySpecifyCode";
import FaceRecorderEmptySpecifyCode from "./FaceRecorder/FaceRecorderEmptySpecifyCode";
import { SelectType } from "./hook";
import { notMinScreen } from "utility/mediaQueries";
import useIsMobile from "hooks/useIsMobile";
import DataTableMobile from "components/organismos/DataTable/DataTableMobile";
import useComponentVisible from "hooks/useComponentVisible";
import StaffSettingPanel from "components/organismos/Appbar/StaffSettingPanel";
import { MobileSidebar } from "components/templates/MobileSidebar";
import FormTitle from "components/atoms/Form/FormTitle";

const isIpad = window.matchMedia("(max-width: 1024px)").matches;

const styles = {
  backHistory: css({
    position: "relative",
    top: "24px",
    left: "-65px",
    display: "inline-block",
    ["&:hover svg"]: {
      opacity: 0.7,
    },
  }),
  container: css({
    maxWidth: "1140px",
    margin: "auto",
    width: "calc(100% - 30px)",
    display: "flex",
    gap: "16px",
    flexDirection: "column",
  }),
  shopName: css({
    paddingTop: "12px",
    marginRight: "150px",
    [notMinScreen("lg")]: {
      marginRight: "20px",
    },
    fontSize: "18px",
    color: "black",
  }),
  tabControl: css({
    display: "flex",
    justifyContent: "space-between",
    marginLeft: "100px",
    ["div"]: {
      width: "290px",
    },
    ["label"]: {
      border: 0,
    },
    ["label:hover"]: {
      background: "#e7f1fd",
      borderRadius: "10px",
      border: 0,
    },
    [notMinScreen("lg")]: {
      marginLeft: "0px",
    },
  }),
  tableCells: css({
    ["table th:nth-of-type(1)"]: {
      width: "90px",
    },
    ["table th:nth-of-type(2)"]: {
      width: "70px",
    },
    ["table th:nth-of-type(3)"]: {
      width: "90px",
    },
    ["table th:nth-of-type(4)"]: {
      width: "80px",
    },
    ["table th:nth-of-type(5)"]: {
      width: "70px",
    },
    ["table th:nth-of-type(6)"]: {
      width: "100px",
    },
    ["table th:nth-of-type(7)"]: {
      width: "110px",
    },
  }),
};

//Context global variable
type contextType = {
  companyCode: string;
  shopCode: string;
  stampByBelongOrg: number;
};

export const TimeRecorderContext = createContext<contextType>({} as contextType);

const Recorder: React.FC = () => {
  const history = useHistory();
  const { staffName } = useLoginUser();
  const isMobile = useIsMobile();
  const [showMobileMenu, setShowMobileMenu] = useState<boolean | null>(null);

  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

  const columns: Array<Column<any>> = React.useMemo(
    () => [
      {
        Header: () => "氏名",
        accessor: "staffName",
        id: "staffName",
        overFlow: true,
        Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) => {
          const [imageOpen, setImageOpen] = useState(false);
          const { staffCode, staffName, stampTime, stampName } = cell.row.original;
          const imageURL = cell.row.original.photoFullpath;
          return (
            <FlexBox customStyle={css({ display: `${isIpad ? "block" : ""}` })}>
              <div>
                {imageURL && (
                  <React.Fragment>
                    <div
                      style={{
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        overflowWrap: "anywhere",
                        justifyContent: `${isIpad ? "center" : ""}`,
                      }}
                    >
                      <img
                        src={cell.row.original.stampMode === 5 ? "data:image/png;base64," + imageURL : imageURL}
                        alt={imageURL}
                        width={55}
                        height={55}
                        style={{
                          cursor: "pointer",
                          borderRadius: "50%",
                          marginRight: "15px",
                        }}
                        onClick={() => setImageOpen(true)}
                      />
                      <ImageModel
                        open={imageOpen}
                        closeHandler={() => {
                          setImageOpen(false);
                        }}
                        imageURL={cell.row.original.stampMode === 5 ? "data:image/png;base64," + imageURL : imageURL}
                        descript={
                          staffCode +
                          "/" +
                          staffName +
                          "/" +
                          stampName +
                          "/" +
                          moment(stampTime).format("YYYY/MM/DD HH:mm")
                        }
                      />
                    </div>
                  </React.Fragment>
                )}
              </div>
              <TextTooltip
                text={cell.row.original.staffName}
                customCss={css({
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  maxWidth: `${imageURL ? "70px" : "130px"}`,
                  whiteSpace: "nowrap",
                  display: "block",
                  margin: "0px auto",
                })}
              />
            </FlexBox>
          );
        },
      },
      {
        Header: "スタッフコード",
        accessor: "staffCode",
        overFlow: true,
      },
      {
        Header: "組織名",
        accessor: "orgName",
        overFlow: true,
      },
      {
        Header: "打刻方法",
        accessor: "stampModeName",
        id: "stampModeName",
        Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) => {
          return (
            <React.Fragment>
              <div style={{ width: "100%" }}>{cell.row.original.stampModeName}</div>
              {cell.row.original.errorMessage !== null && cell.row.original.errorMessage !== "" && (
                <BodyText customStyle={css({ fontSize: "13px", color: "#333333" })}>
                  {cell.row.original.cardId}
                </BodyText>
              )}
            </React.Fragment>
          );
        },
      },
      {
        Header: () => "打刻種別",
        accessor: "errorMessage",
        id: "errorMessage",
        Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) => {
          const { stampName, isRemote } = cell.row.original;
          if (cell.row.original.errorMessage === null || cell.row.original.errorMessage === "") {
            return (
              <div>
                {stampName === "出勤"
                  ? isRemote
                    ? stampName + `${companyCode === "HPo6wkyuze" ? " (在宅)" : " (テレワーク)"}`
                    : stampName + " (オフィス)"
                  : stampName}
              </div>
            );
          }
          if (cell.row.original.errorMessage !== null && cell.row.original.errorMessage !== "") {
            return (
              <span>
                <div css={css({ color: utilityColor.note })} data-tip={cell.row.original.errorMessage}>
                  {stampName}
                </div>
                <ReactTooltip html={true} />
              </span>
            );
          }
        },
      },
      {
        Header: () => "打刻時刻",
        accessor: "stampTime",
        id: "stampTime",
        Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) =>
          cell.row.original.stampTime ? moment(cell.row.original.stampTime).format("YYYY/MM/DD HH:mm:ss") : "",
      },
    ],
    [],
  );

  const mobileColumns: Array<Column<any>> = React.useMemo(
    () => [
      {
        Header: () => "氏名",
        accessor: "staffName",
        id: "staffName",
        overFlow: true,
        Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) => {
          const [imageOpen, setImageOpen] = useState(false);
          const { staffCode, staffName, stampTime, stampName } = cell.row.original;
          const imageURL = cell.row.original.photoFullpath;
          return (
            <FlexBox customStyle={css({ display: `${isIpad ? "block" : ""}` })}>
              <div>
                {imageURL && (
                  <React.Fragment>
                    <div
                      style={{
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        overflowWrap: "anywhere",
                        justifyContent: `${isIpad ? "center" : ""}`,
                      }}
                    >
                      <img
                        src={cell.row.original.stampMode === 5 ? "data:image/png;base64," + imageURL : imageURL}
                        alt={imageURL}
                        width={55}
                        height={55}
                        style={{
                          cursor: "pointer",
                          borderRadius: "50%",
                          marginRight: "15px",
                        }}
                        onClick={() => setImageOpen(true)}
                      />
                      <ImageModel
                        open={imageOpen}
                        closeHandler={() => {
                          setImageOpen(false);
                        }}
                        imageURL={cell.row.original.stampMode === 5 ? "data:image/png;base64," + imageURL : imageURL}
                        descript={
                          staffCode +
                          "/" +
                          staffName +
                          "/" +
                          stampName +
                          "/" +
                          moment(stampTime).format("YYYY/MM/DD HH:mm")
                        }
                      />
                    </div>
                  </React.Fragment>
                )}
              </div>
              <TextTooltip
                text={cell.row.original.staffName}
                customCss={css({
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  maxWidth: `${imageURL ? "70px" : "130px"}`,
                  whiteSpace: "nowrap",
                  display: "block",
                  margin: "0px auto",
                })}
              />
            </FlexBox>
          );
        },
      },

      {
        Header: () => "打刻種別",
        accessor: "errorMessage",
        id: "errorMessage",
        Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) => {
          const { stampName, isRemote } = cell.row.original;
          if (cell.row.original.errorMessage === null || cell.row.original.errorMessage === "") {
            return (
              <div>
                {stampName === "出勤"
                  ? isRemote
                    ? stampName + `${companyCode === "HPo6wkyuze" ? " (在宅)" : " (テレワーク)"}`
                    : stampName + " (オフィス)"
                  : stampName}
              </div>
            );
          }
          if (cell.row.original.errorMessage !== null && cell.row.original.errorMessage !== "") {
            return (
              <span>
                <div css={css({ color: utilityColor.note })} data-tip={cell.row.original.errorMessage}>
                  {stampName}
                </div>
                <ReactTooltip html={true} />
              </span>
            );
          }
        },
      },
      {
        Header: () => "打刻時刻",
        accessor: "stampTime",
        id: "stampTime",
        Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) =>
          cell.row.original.stampTime ? moment(cell.row.original.stampTime).format("YYYY/MM/DD HH:mm:ss") : "",
      },
    ],
    [],
  );

  const [isLoading, setIsLoading] = useState(false);
  const {
    stampHistoryList,
    currentDay,
    timeNow,
    second,
    stampOrgCode,
    stampOrgName,
    loginOrgCode,
    loginOrgName,
    setStampOrgCode,
    setStampOrgName,
    setLoginOrgCode,
    setLoginOrgName,
    resultCurrentTime,
    useFaceStamp,
    useBarcodeStamp,
    useICCardStamp,
    disabledRecorder,
    fetchHistoryData,
    companyCode,
    specifyCode,
  } = useRecorder(setIsLoading);

  const dataTimes = {
    timeNow,
    currentDay,
    second,
    resultCurrentTime,
  };
  const dataSettings = {
    stampOrgCode,
    setStampOrgCode,
    stampOrgName,
    setStampOrgName,
    loginOrgCode,
    setLoginOrgCode,
    loginOrgName,
    setLoginOrgName,
    useFaceStamp,
    useBarcodeStamp,
    useICCardStamp,
  };

  const [tabs, setTabs] = useState<{ value: SelectType; display: string }[]>([]);

  useEffect(() => {
    const tempTabs = [];

    if (useICCardStamp && !isMobile) {
      tempTabs.push({
        value: SelectType.icCard,
        display: "ICカード",
      });
    }
    if (useBarcodeStamp) {
      tempTabs.push({
        value: SelectType.id,
        display: "ID",
      });
    }

    if (useFaceStamp && !isMobile) {
      tempTabs.push({
        value: SelectType.face,
        display: "顔認証",
      });
    }

    setSelectType(tempTabs[0]?.value);
    setTabs(tempTabs);
  }, [useICCardStamp, useBarcodeStamp, useFaceStamp, isMobile]);

  const [selectType, setSelectType] = useState<SelectType>(SelectType.id);

  return (
    <React.Fragment>
      <TimeRecorderContext.Provider
        value={{
          companyCode: companyCode,
          shopCode: stampOrgCode,
          stampByBelongOrg: 0,
        }}
      >
        <BlockUI blocking={isLoading}>
          <Appbar
            staffName={staffName}
            title="打刻"
            changeVisibleEvent={(visible: boolean) => {
              if (isMobile) {
                setShowMobileMenu(visible);
              } else {
                setIsComponentVisible(visible);
              }
            }}
          />
          <div css={styles.container}>
            {!isMobile && (
              <div css={styles.backHistory} onClick={() => history.goBack()}>
                <Icon type="back" color="#666" />
              </div>
            )}
            <FormTitle title="打刻" bold customStyle={css(isMobile ? { padding: "8px 0 0 0", margin: 0 } : {})} />
            {Number(useBarcodeStamp) !== 0 || Number(useFaceStamp) !== 0 || Number(useICCardStamp) !== 0 ? (
              <FormContents>
                <div css={styles.tabControl}>
                  {tabs && (
                    <TabControl
                      styleActive={{
                        backgroundColor: "#E7F1FD",
                        borderRadius: "10px",
                      }}
                      items={tabs}
                      setValue={setSelectType as (arg: string) => void}
                      currentValue={selectType}
                    ></TabControl>
                  )}
                </div>
                {selectType === SelectType.icCard && (
                  <React.Fragment>
                    {specifyCode === "" ? (
                      <ICCardRecorderEmptySpecifyCode
                        dataTimes={dataTimes}
                        setIsLoading={setIsLoading}
                        fetchHistoryData={fetchHistoryData}
                      />
                    ) : (
                      <React.Fragment>
                        {Boolean(useICCardStamp) && (
                          <ICCardRecorder
                            dataTimes={dataTimes}
                            dataSettings={dataSettings}
                            setIsLoading={setIsLoading}
                            fetchHistoryData={fetchHistoryData}
                          />
                        )}
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}
                {selectType === SelectType.id && (
                  <React.Fragment>
                    {specifyCode === "" ? (
                      <BarcodeRecorderEmptySpecifyCode
                        dataTimes={dataTimes}
                        setIsLoading={setIsLoading}
                        fetchHistoryData={fetchHistoryData}
                      />
                    ) : (
                      <React.Fragment>
                        {Boolean(useBarcodeStamp) && (
                          <BarcodeRecorder
                            dataTimes={dataTimes}
                            dataSettings={dataSettings}
                            setIsLoading={setIsLoading}
                            disabledRecorder={disabledRecorder}
                            fetchHistoryData={fetchHistoryData}
                          />
                        )}
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}

                {selectType === SelectType.face && (
                  <React.Fragment>
                    {specifyCode === "" ? (
                      <FaceRecorderEmptySpecifyCode
                        dataTimes={dataTimes}
                        setIsLoading={setIsLoading}
                        fetchHistoryData={fetchHistoryData}
                      />
                    ) : (
                      <React.Fragment>
                        {Boolean(useFaceStamp) && (
                          <FaceRecorder
                            dataTimes={dataTimes}
                            dataSettings={dataSettings}
                            setIsLoading={setIsLoading}
                            disabledRecorder={disabledRecorder}
                            fetchHistoryData={fetchHistoryData}
                          />
                        )}
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}
              </FormContents>
            ) : (
              ""
            )}

            <FormContents customStyle={css({ borderRadius: "3px" })}>
              <div css={css({ marginLeft: "28px" })}>
                <FormLabel label="打刻履歴" customStyle={css({ fontWeight: "bold" })} />
              </div>
              {isMobile ? (
                <DataTableMobile
                  columns={mobileColumns}
                  data={stampHistoryList}
                  isGlobalFilter={true}
                  globalSearchStyle={css({
                    borderRadius: "3px",
                  })}
                />
              ) : (
                <div css={styles.tableCells}>
                  <DataTable
                    columns={columns}
                    data={stampHistoryList}
                    isGlobalFilter={true}
                    globalSearchStyle={css({
                      marginLeft: "28px",
                      borderRadius: "3px",
                    })}
                  />
                </div>
              )}
            </FormContents>
          </div>
        </BlockUI>
        <div ref={ref} css={css({ position: "absolute", top: 0, right: "2rem" })}>
          {isComponentVisible && <StaffSettingPanel staffName={staffName} />}
        </div>
      </TimeRecorderContext.Provider>
      {isMobile && <MobileSidebar isMenuOpen={showMobileMenu} setMenuOpen={(open) => setShowMobileMenu(open)} />}
    </React.Fragment>
  );
};

export default Recorder;
